import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { uploadDevStandardsSchema } from '../schema/devStandardsSchema';
import type { UploadDevStandardsFormValues } from '../schema/devStandardsSchema';
import { useUploadStandards } from '../hooks/useUploadStandards';
import { MarkdownView } from './MarkdownView';

interface UploadStandardsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type InputMode = 'file' | 'paste';

export function UploadStandardsDialog({
  open,
  onClose,
  onSuccess,
}: UploadStandardsDialogProps) {
  const { t, i18n } = useTranslation();
  const [inputMode, setInputMode] = useState<InputMode>('paste');
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadStandards();

  const todayStr = new Intl.DateTimeFormat(
    i18n.language === 'en' ? 'en-CA' : 'ko-KR',
    { year: 'numeric', month: '2-digit', day: '2-digit' }
  )
    .format(new Date())
    .replace(/\//g, '-');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UploadDevStandardsFormValues>({
    resolver: zodResolver(uploadDevStandardsSchema),
    defaultValues: {
      markdownText: '',
      docVersion: todayStr,
      sourceCommit: '',
    },
  });

  const markdownText = watch('markdownText');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setValue('markdownText', text, { shouldValidate: true });
  };

  const onSubmit = async (values: UploadDevStandardsFormValues) => {
    await uploadMutation.mutateAsync({
      markdownText: values.markdownText,
      docVersion: values.docVersion || undefined,
      sourceCommit: values.sourceCommit || undefined,
    });
    reset();
    onSuccess();
    onClose();
  };

  const handleClose = () => {
    reset();
    setPreviewOpen(false);
    onClose();
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-1)',
    fontFamily: 'var(--font-family-sans)',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid var(--color-surface-border)`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family-sans)',
    background: 'var(--color-surface-background)',
    color: 'var(--color-text-primary)',
    outline: 'none',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <FormDialog
      open={open}
      title={t('devStandardsViewer.upload.dialog.title')}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={() => void handleSubmit(onSubmit)()}
      submitLabel={
        isSubmitting || uploadMutation.isPending
          ? t('devStandardsViewer.upload.submit.loading')
          : t('devStandardsViewer.upload.submit')
      }
      cancelLabel={t('devStandardsViewer.upload.cancel')}
      submitTestId="upload-standards-submit"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>

        {/* 입력 모드 전환 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          {(['paste', 'file'] as InputMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setInputMode(mode)}
              aria-pressed={inputMode === mode}
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-full)',
                background:
                  inputMode === mode
                    ? 'var(--color-brand-primary)'
                    : 'var(--color-surface-background)',
                color:
                  inputMode === mode
                    ? 'var(--color-text-inverse)'
                    : 'var(--color-text-secondary)',
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-family-sans)',
                cursor: 'pointer',
              }}
            >
              {mode === 'paste'
                ? t('devStandardsViewer.upload.mode.paste')
                : t('devStandardsViewer.upload.mode.file')}
            </button>
          ))}
        </div>

        {/* 파일 picker 또는 textarea */}
        {inputMode === 'file' ? (
          <div>
            <label htmlFor="upload-file-input" style={labelStyle}>
              {t('devStandardsViewer.upload.file.label')}
            </label>
            <input
              id="upload-file-input"
              ref={fileRef}
              type="file"
              accept=".md,text/markdown,text/plain"
              onChange={(e) => void handleFileChange(e)}
              style={{
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                color: 'var(--color-text-primary)',
              }}
              aria-describedby={errors.markdownText ? 'markdown-error' : undefined}
              aria-invalid={!!errors.markdownText}
            />
            {markdownText && (
              <div
                style={{
                  marginTop: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-success)',
                }}
              >
                {markdownText.length.toLocaleString()}자 로드됨
              </div>
            )}
            {errors.markdownText && (
              <p id="markdown-error" style={errorStyle} role="alert">
                {errors.markdownText.message}
              </p>
            )}
          </div>
        ) : (
          <div>
            <label htmlFor="upload-paste-input" style={labelStyle}>
              {t('devStandardsViewer.upload.paste.label')}
            </label>
            <textarea
              id="upload-paste-input"
              rows={8}
              placeholder={t('devStandardsViewer.upload.paste.placeholder')}
              aria-describedby={errors.markdownText ? 'markdown-error' : undefined}
              aria-invalid={!!errors.markdownText}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}
              {...register('markdownText')}
            />
            {errors.markdownText && (
              <p id="markdown-error" style={errorStyle} role="alert">
                {errors.markdownText.message}
              </p>
            )}
          </div>
        )}

        {/* docVersion */}
        <div>
          <label htmlFor="upload-version-input" style={labelStyle}>
            {t('devStandardsViewer.upload.docVersion.label')}
          </label>
          <input
            id="upload-version-input"
            type="text"
            placeholder={todayStr}
            style={inputStyle}
            aria-invalid={!!errors.docVersion}
            aria-describedby={errors.docVersion ? 'version-error' : undefined}
            {...register('docVersion')}
          />
          {errors.docVersion && (
            <p id="version-error" style={errorStyle} role="alert">
              {errors.docVersion.message}
            </p>
          )}
        </div>

        {/* sourceCommit */}
        <div>
          <label htmlFor="upload-commit-input" style={labelStyle}>
            {t('devStandardsViewer.upload.sourceCommit.label')}
          </label>
          <input
            id="upload-commit-input"
            type="text"
            placeholder="a1b2c3d (선택)"
            style={{ ...inputStyle, fontFamily: 'var(--font-family-mono)' }}
            aria-invalid={!!errors.sourceCommit}
            aria-describedby={errors.sourceCommit ? 'commit-error' : undefined}
            {...register('sourceCommit')}
          />
          {errors.sourceCommit && (
            <p id="commit-error" style={errorStyle} role="alert">
              {errors.sourceCommit.message}
            </p>
          )}
        </div>

        {/* 미리보기 */}
        {markdownText && (
          <div>
            <button
              type="button"
              onClick={() => setPreviewOpen((v) => !v)}
              aria-expanded={previewOpen}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-brand-primary)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
              }}
            >
              <span style={{ transform: previewOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
              {t('devStandardsViewer.upload.preview.toggle')}
            </button>
            {previewOpen && (
              <div
                style={{
                  marginTop: 'var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-4)',
                  maxHeight: 300,
                  overflowY: 'auto',
                  background: 'var(--color-surface-muted)',
                }}
              >
                <MarkdownView markdownText={markdownText} />
              </div>
            )}
          </div>
        )}

        {/* 업로드 에러 */}
        {uploadMutation.isError && (
          <p style={{ ...errorStyle, marginTop: 0 }} role="alert">
            {(uploadMutation.error as { message?: string })?.message ?? '업로드에 실패했습니다.'}
          </p>
        )}
      </div>
    </FormDialog>
  );
}
